import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getApiErrorMessage } from 'src/api/axiosClient/errorParser';
import { ApiErrorWithResponse } from 'src/api/axiosClient/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { buildIntegrationFormData, buildIntegrationRequestData } from '../helpers';
import { useIntegrationService, useIntegrationsService } from '../services';
import { HookEventType, Integration, IntegrationFormData } from '../types';

export interface AddIntegrationModalController {
  isOpen: boolean;
  initFormData: IntegrationFormData | null;
  hookEventTypes: HookEventType[];
  open: (integration?: Integration) => void;
  close: () => void;
  submit: (
    formData: IntegrationFormData,
  ) => Promise<{ validationError?: string } | void>;
}

export const useAddIntegrationModalController = (): AddIntegrationModalController => {
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const [initFormData, setInitFormData] = useState<IntegrationFormData | null>(null);
  const [hookEventTypes, setHookEventTypes] = useState<HookEventType[]>([]);

  const { getHookEventTypes } = useIntegrationsService();
  const { createIntegration, updateIntegration } = useIntegrationService();

  const open = useCallback((integration?: Integration) => {
    setInitFormData(buildIntegrationFormData(integration));
  }, []);

  const close = useCallback(() => {
    setInitFormData(null);
  }, []);

  const submit = useCallback(
    async (formData: IntegrationFormData) => {
      if (!selectedCompanyId) {
        return;
      }

      const requestData = buildIntegrationRequestData(formData);

      try {
        if (formData.id) {
          await updateIntegration(requestData, formData.id, selectedCompanyId);
        } else {
          await createIntegration(requestData, selectedCompanyId);
        }
        close();
      } catch (e: unknown) {
        const errorCode = (e as ApiErrorWithResponse)?.response?.data?.error ?? (e as ApiErrorWithResponse)?.error;
        if (errorCode === 'conflict') {
          return { validationError: 'Integration with the same name already exists!' };
        }
        return { validationError: getApiErrorMessage(e, 'Something went wrong!') };
      }
    },
    [selectedCompanyId, createIntegration, updateIntegration, close],
  );

  useEffect(() => {
    getHookEventTypes()
      .then(setHookEventTypes)
      .catch(e => console.log(e));
  }, [getHookEventTypes]);

  return {
    isOpen: !!initFormData,
    initFormData,
    hookEventTypes,
    open,
    close,
    submit,
  };
};
